import { createFileRoute } from "@tanstack/react-router";
import { Loader2, ClipboardList, Eye } from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { API_BASE_URL } from "@/lib/api";

export const Route = createFileRoute("/admin/orders")({
  head: () => ({
    meta: [
      { title: "Manage Orders — Royal Lumina" },
    ],
  }),
  component: AdminOrders,
});

// Mock orders for offline fallback
const mockOrders = [
  {
    id: "ord_101",
    user: { name: "Aarav Sharma", email: "aarav@gmail.com" },
    created_at: "2026-07-04T12:00:00Z",
    totalAmount: 1798,
    status: "processing",
    paymentStatus: "paid",
    shippingAddress: { address: "101, Luxury Apartments", city: "Mumbai", zip: "400001" },
    items: [
      { id: "1", product: { name: "Rose Couple Candle" }, qty: 2, price: 899 }
    ]
  },
  {
    id: "ord_102",
    user: { name: "Priya Patel", email: "priya@yahoo.com" },
    created_at: "2026-07-03T15:30:00Z",
    totalAmount: 649,
    status: "shipped",
    paymentStatus: "paid",
    shippingAddress: { address: "A-502, Green Meadows", city: "Pune", zip: "411001" },
    items: [
      { id: "2", product: { name: "Pineapple Sculpt" }, qty: 1, price: 649 }
    ]
  },
  {
    id: "ord_103",
    user: { name: "Kabir Mehta", email: "kabir@outlook.com" },
    created_at: "2026-07-03T09:15:00Z",
    totalAmount: 2398,
    status: "delivered",
    paymentStatus: "paid",
    shippingAddress: { address: "Flat 4B, Royale Heights", city: "Delhi", zip: "110001" },
    items: [
      { id: "3", product: { name: "Crystal Glow Votives" }, qty: 3, price: 799 }
    ]
  }
];

function AdminOrders() {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("supabase.auth.token") || "";
      const res = await fetch(`${API_BASE_URL}/api/orders`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setOrders(data);
      } else {
        setOrders(mockOrders);
      }
    } catch (err) {
      setOrders(mockOrders);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      const token = localStorage.getItem("supabase.auth.token") || "";
      const res = await fetch(`${API_BASE_URL}/api/orders/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        toast.success(`Order status updated to ${newStatus}`);
        fetchOrders();
        if (selectedOrder && selectedOrder.id === id) {
          setSelectedOrder({ ...selectedOrder, status: newStatus });
        }
      } else {
        throw new Error("Update failed");
      }
    } catch (err) {
      // Simulate state updates locally if backend offline
      setOrders((prev) => 
        prev.map((o) => o.id === id ? { ...o, status: newStatus } : o)
      );
      toast.success(`Status updated to ${newStatus} (Local simulation)`);
      if (selectedOrder && selectedOrder.id === id) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
    }
  };

  const handleOpenView = (order: any) => {
    setSelectedOrder(order);
    setIsViewOpen(true);
  };

  return (
    <div className="space-y-8">
      {/* Header bar */}
      <div>
        <h2 className="font-display text-4xl tracking-tight text-primary">Orders Management</h2>
        <p className="text-sm text-muted-foreground mt-1">Track payments, package shipments and delivery statuses.</p>
      </div>

      {/* Orders Table */}
      {loading ? (
        <div className="h-64 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : (
        <div className="bg-card border border-border rounded-[24px] overflow-hidden shadow-luxe">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="bg-muted/40 border-b border-border text-[0.65rem] uppercase tracking-wider text-muted-foreground font-semibold">
                  <th className="px-6 py-4">Order ID</th>
                  <th className="px-6 py-4">Customer Details</th>
                  <th className="px-6 py-4">Order Date</th>
                  <th className="px-6 py-4 text-right">Total Price</th>
                  <th className="px-6 py-4 text-center">Status</th>
                  <th className="px-6 py-4 text-center">Update Status</th>
                  <th className="px-6 py-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {orders.map((o) => (
                  <tr key={o.id} className="hover:bg-muted/10 transition-colors">
                    <td className="px-6 py-4 font-mono text-xs">{o.id}</td>
                    <td className="px-6 py-4">
                      <div className="font-semibold text-primary">{o.user?.name || "Guest Customer"}</div>
                      <div className="text-xs text-muted-foreground">{o.user?.email}</div>
                    </td>
                    <td className="px-6 py-4 text-muted-foreground">
                      {new Date(o.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right font-medium">
                      <div className="text-primary">₹{o.totalAmount.toLocaleString()}</div>
                      <div className="text-[0.65rem] uppercase tracking-wider text-emerald-600 font-bold">{o.paymentStatus}</div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[0.65rem] font-bold uppercase tracking-wider ${
                        o.status === "delivered" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                        o.status === "shipped" ? "bg-blue-50 text-blue-700 border border-blue-200" :
                        o.status === "processing" ? "bg-amber-50 text-amber-700 border border-amber-200" :
                        "bg-rose-50 text-rose-700 border border-rose-200"
                      }`}>
                        {o.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <select
                        value={o.status}
                        onChange={(e) => handleStatusChange(o.id, e.target.value)}
                        className="h-9 px-2 bg-card border border-border rounded-lg text-xs focus:outline-none focus:border-gold"
                      >
                        <option value="pending">Pending</option>
                        <option value="processing">Processing</option>
                        <option value="shipped">Shipped</option>
                        <option value="delivered">Delivered</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => handleOpenView(o)}
                        className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider"
                      >
                        <Eye className="h-4 w-4" />
                        <span>Inspect</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Inspect Order Dialog */}
      {isViewOpen && selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-primary/40 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-[24px] max-w-2xl w-full p-8 shadow-luxe relative overflow-y-auto max-h-[90vh]">
            <div className="flex justify-between items-start mb-6">
              <div>
                <span className="eyebrow">Order Inspection</span>
                <h3 className="font-display text-2xl text-primary mt-1">ID: {selectedOrder.id}</h3>
              </div>
              <button
                onClick={() => setIsViewOpen(false)}
                className="h-8 w-8 rounded-full border border-border grid place-items-center hover:border-gold text-sm font-semibold"
              >
                ✕
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-8 py-4 border-t border-b border-border">
              {/* Left Column: Customer details */}
              <div className="space-y-4">
                <div>
                  <div className="text-[0.6rem] tracking-[0.2em] uppercase text-muted-foreground font-semibold">Recipient Customer</div>
                  <div className="text-sm font-semibold text-primary mt-1">{selectedOrder.user?.name || "Guest Customer"}</div>
                  <div className="text-xs text-muted-foreground">{selectedOrder.user?.email}</div>
                </div>

                <div>
                  <div className="text-[0.6rem] tracking-[0.2em] uppercase text-muted-foreground font-semibold">Shipping Destination Address</div>
                  <div className="text-sm mt-1 text-primary">
                    {selectedOrder.shippingAddress?.address || "No address provided"}, {selectedOrder.shippingAddress?.city}, Pin {selectedOrder.shippingAddress?.zip}
                  </div>
                </div>
              </div>

              {/* Right Column: Order stats */}
              <div className="space-y-4">
                <div>
                  <div className="text-[0.6rem] tracking-[0.2em] uppercase text-muted-foreground font-semibold">Financial & Delivery Status</div>
                  <div className="mt-1 flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-[0.65rem] font-bold uppercase tracking-wider ${
                      selectedOrder.paymentStatus === "paid" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-rose-50 text-rose-700 border border-rose-200"
                    }`}>
                      {selectedOrder.paymentStatus}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-[0.65rem] font-bold uppercase tracking-wider ${
                      selectedOrder.status === "delivered" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"
                    }`}>
                      {selectedOrder.status}
                    </span>
                  </div>
                </div>

                <div>
                  <div className="text-[0.6rem] tracking-[0.2em] uppercase text-muted-foreground font-semibold">Quick Actions</div>
                  <div className="mt-2 flex gap-2">
                    <button
                      onClick={() => handleStatusChange(selectedOrder.id, "shipped")}
                      className="px-3 py-1.5 rounded-lg border border-border hover:border-gold hover:text-gold text-xs font-semibold uppercase tracking-wider"
                    >
                      Ship Order
                    </button>
                    <button
                      onClick={() => handleStatusChange(selectedOrder.id, "delivered")}
                      className="px-3 py-1.5 rounded-lg bg-gold text-primary hover:bg-gold-light text-xs font-semibold uppercase tracking-wider"
                    >
                      Deliver Order
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Product items inside order */}
            <div className="mt-6">
              <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground font-semibold mb-3">Cart Contents</div>
              <div className="space-y-3">
                {selectedOrder.items?.map((item: any, i: number) => (
                  <div key={i} className="flex justify-between items-center py-2 text-sm border-b border-border/40 last:border-0">
                    <div>
                      <div className="font-semibold text-primary">{item.product?.name || "Royal Candle"}</div>
                      {item.variant && <div className="text-xs text-muted-foreground">Color Variant: {item.variant}</div>}
                    </div>
                    <div className="text-right">
                      <div className="font-medium text-primary">₹{item.price.toLocaleString()} × {item.qty}</div>
                      <div className="text-xs text-muted-foreground">Subtotal: ₹{(item.price * item.qty).toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Total Footer */}
            <div className="mt-6 pt-4 border-t border-border flex justify-between items-center font-display text-xl text-primary font-bold">
              <span>Grand Total</span>
              <span>₹{selectedOrder.totalAmount.toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
